
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { editionTradesPopulation, getArtwork } from 'api/artworks';
import routes from 'constants/routes';
import { useTempAddress } from 'contexts/stores/TempAddressStore';
import { ArtworkTradeLayout } from 'layouts/ArtworkTradeLayout';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import qs from 'qs';
import { ReactElement, useEffect, useMemo } from 'react';
import { ArtworkRoyalty } from 'types';
import { Artwork, Edition, mapIArtistsToArtists, mapIArtworkToEditions, } from 'utils/data';
const ArtworkTradeForm = dynamic(() => import('components/forms/ArtworkTradeForm').then(m => m.ArtworkTradeForm), { ssr: false });
const ReviewTradeForm = dynamic(() => import('components/forms/ReviewTradeForm').then(m => m.ReviewTradeForm), { ssr: false });
const ShippingAndBillingForm = dynamic(() => import('components/forms/ShippingAndBillingForm').then(m => m.ShippingAndBillingForm), { ssr: false });
const StripeSetupProvider = dynamic(() => import('contexts/providers/StripeSetupProvider').then(m => m.StripeSetupProvider), { ssr: false });
type Props = {
    artwork: Artwork;
    currentEdition: Edition;
};
const BuyOrBid = ({ artwork, currentEdition }: Props) => {
    const router = useRouter();
    const { setTempAddress } = useTempAddress();
    const { isShippingAndBillingPage, isReviewPage } = useMemo(() => {
        const isShippingAndBillingPage = router.asPath.startsWith(routes.bidOrBuy.shippingAndBilling);
        const isReviewPage = router.asPath.startsWith(routes.bidOrBuy.reviewBid);
        return { isShippingAndBillingPage, isReviewPage };
    }, [router.asPath]);
    useEffect(() => {
        if (isReviewPage) {
            const handleBeforeUnload = (event: BeforeUnloadEvent) => {
                // Custom message, though some browsers may ignore it
                const message = 'Are you sure you want to leave? Changes you made may not be saved.';
                event.returnValue = message; // Standard for most browsers
                return message; // For some browsers (e.g., Chrome)
            };
            window.addEventListener('beforeunload', handleBeforeUnload);
            // Cleanup event listener on component unmount
            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
            };
        }
    }, [isReviewPage]);
    useEffect(() => {
        function maybeRemoveTempAddress(url: string) {
            if (url.includes('/thanks') || url.includes('/bid-or-buy')) {
                return;
            }
            setTempAddress(null);
        }
        router.events.on('routeChangeStart', maybeRemoveTempAddress);
        return () => {
            router.events.off('routeChangeStart', maybeRemoveTempAddress);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.events]);
    return (<>
      <Head>
        <meta name='robots' content='noindex'/>
      </Head>

      {!isShippingAndBillingPage && !isReviewPage && (<ArtworkTradeForm type='bid-or-buy' artwork={artwork} currentEdition={currentEdition}/>)}

      {isShippingAndBillingPage && (<StripeSetupProvider>
          <ShippingAndBillingForm artwork={artwork} currentEdition={currentEdition}/>
        </StripeSetupProvider>)}

      {isReviewPage && (<StripeSetupProvider>
          <ReviewTradeForm artwork={artwork} currentEdition={currentEdition}/>
        </StripeSetupProvider>)}
    </>);
};
BuyOrBid.getLayout = (page: ReactElement) => (<ArtworkTradeLayout type='bid-or-buy' artwork={page.props.artwork} currentEdition={page.props.currentEdition}>
    {page}
  </ArtworkTradeLayout>);
const fetchData = async (id: number) => {
    const query = qs.stringify({
        populate: {
            images: {
                fields: '*',
                populate: {
                    formats: true
                }
            },
            editions: {
                fields: '*',
                populate: {
                    asks: true,
                    bids: true,
                    additionalCertification: true,
                    includes: true,
                    features: true,
                    size: true,
                    ...editionTradesPopulation
                }
            },
            gallery: true,
            artists: {
                fields: '*',
                populate: {
                    nationalities: true
                }
            }
        }
    }, {
        encodeValuesOnly: true
    });
    const artworkData = await getArtwork(id, query);
    const name = artworkData.data.attributes.name as string;
    const images = artworkData?.data?.attributes?.images?.data?.map(img => ({
        id: img.id,
        ...img.attributes
    })) || [];
    const editions = mapIArtworkToEditions(artworkData?.data) || {};
    const artists = mapIArtistsToArtists(artworkData?.data?.attributes?.artists?.data);
    const royalty = artworkData?.data?.attributes?.royalty as ArtworkRoyalty;
    return { name, artists, editions, images, royalty };
};
const getServerSideProps: GetServerSideProps<Props> = async ({ params, }) => {
    const slug = params?.slug;
    if (!slug) {
        return { notFound: true };
    }
    const id = Number(slug[slug.length - 2]);
    if (isNaN(id)) {
        return { notFound: true };
    }
    const editionName = slug[slug.length - 1];
    if (typeof editionName !== 'string') {
        return { notFound: true };
    }
    try {
        const data = await fetchData(id);
        const currentEdition = data.editions.find(edition => edition.name === editionName);
        if (!currentEdition) {
            return { notFound: true };
        }
        return {
            props: {
                artwork: {
                    id,
                    ...data
                },
                currentEdition
            }
        };
    }
    catch (error) {
        console.error(error);
        return { notFound: true };
    }
};
export default BuyOrBid;

    async function __Next_Translate__getServerSideProps__19bb23d2e03__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/bid-or-buy/[...slug]',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__19bb23d2e03__ as getServerSideProps }
  